// Carte du curseur : correspondance entre le temps musical (ticks) et la position
// horizontale sur la partition gravée. Les ancres sont les positions des notes, des
// silences et des barres de mesure ; entre deux ancres, la position est interpolée.

export interface CursorAnchor {
  tick: number;
  x: number;
}

/**
 * Trie les ancres, fusionne les ticks identiques (la position la plus à droite l'emporte :
 * la note plutôt que la barre de mesure qui la précède) et garantit une progression monotone.
 */
export function normalizeAnchors(anchors: readonly CursorAnchor[]): CursorAnchor[] {
  const sorted = anchors.slice().sort((a, b) => a.tick - b.tick || a.x - b.x);
  const out: CursorAnchor[] = [];
  for (const a of sorted) {
    const last = out[out.length - 1];
    if (last && last.tick === a.tick) {
      last.x = Math.max(last.x, a.x);
      continue;
    }
    out.push({ tick: a.tick, x: last ? Math.max(last.x, a.x) : a.x });
  }
  return out;
}

/** Position du curseur pour un tick ; bornée à la première et à la dernière ancre. */
export function xForTick(anchors: readonly CursorAnchor[], tick: number): number {
  if (anchors.length === 0) return 0;
  if (tick <= anchors[0].tick) return anchors[0].x;
  const last = anchors[anchors.length - 1];
  if (tick >= last.tick) return last.x;
  let lo = 0;
  let hi = anchors.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (anchors[mid].tick <= tick) lo = mid;
    else hi = mid;
  }
  const a = anchors[lo];
  const b = anchors[hi];
  if (b.tick === a.tick) return a.x;
  return a.x + ((tick - a.tick) / (b.tick - a.tick)) * (b.x - a.x);
}

/**
 * Décalage de défilement qui place le curseur à une fraction de la largeur visible,
 * sans dépasser les bords de la partition.
 */
export function scrollOffsetFor(cursorX: number, viewportWidth: number, totalWidth: number, fraction = 0.35): number {
  const max = Math.max(0, totalWidth - viewportWidth);
  return Math.max(0, Math.min(max, cursorX - viewportWidth * fraction));
}
